import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Spinner,
  Alert,
  Badge,
} from "reactstrap";


import api from "../../../../../../../services/api";

interface ConectarPlayneeConversasModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnected: () => void;
  numeroAtual?: string | null;
}

type Etapa =
  | "carregando"
  | "numero"
  | "qr"
  | "conectado"
  | "erro";

const STATUS_POLL_MS = 3000;
const STATUS_TIMEOUT_MS = 2 * 60 * 1000;

function onlyDigits(raw: string | null | undefined): string {
  return String(raw || "").replace(/\D/g, "");
}

// Converte qualquer entrada ("(11) 98765-4321", "5511987654321", "+55 11 9 8765-4321")
// para o formato esperado pelo PhoneInput: "55 11 987654321".
function formatParaPhoneInput(raw: string | null | undefined): string {
  let d = onlyDigits(raw).replace(/^0+/, "");
  if (!d) return "";
  if (!d.startsWith("55")) d = "55" + d;
  if (d.length < 12) return "";
  const ddi = d.slice(0, 2);
  const ddd = d.slice(2, 4);
  const resto = d.slice(4);
  return `${ddi} ${ddd} ${resto}`;
}

// Retorna o número em formato canônico (somente dígitos, com DDI 55) ou "" se inválido.
function normalizarParaChave(raw: string | null | undefined): string {
  let d = onlyDigits(raw).replace(/^0+/, "");
  if (!d) return "";
  if (!d.startsWith("55")) d = "55" + d;
  return /^55\d{10,11}$/.test(d) ? d : "";
}

export function ConectarPlayneeConversasModal({
  isOpen,
  onClose,
  onConnected,
  numeroAtual,
}: ConectarPlayneeConversasModalProps) {
  const [numero, setNumero] = useState("");
  const [qrcode, setQrcode] = useState<string | null>(null);
  const [numeroNormalizado, setNumeroNormalizado] = useState<string | null>(null);
  const [etapa, setEtapa] = useState<Etapa>("carregando");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const deadlineRef = useRef<number | null>(null);

  function stopPolling() {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    deadlineRef.current = null;
  }

  function resetInterno() {
    stopPolling();
    setNumero("");
    setQrcode(null);
    setNumeroNormalizado(null);
    setEtapa("carregando");
    setLoading(false);
    setErro(null);
  }

  function handleClose() {
    resetInterno();
    onClose();
  }

  useEffect(() => {
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      stopPolling();
      return;
    }
    setErro(null);
    setQrcode(null);

    const chave = normalizarParaChave(numeroAtual);
    const formatado = formatParaPhoneInput(numeroAtual);

    if (chave) {
      setNumero(formatado);
      verificarStatus(chave);
    } else {
      setNumero("");
      setEtapa("numero");
    }
  }, [isOpen, numeroAtual]);

  async function verificarStatus(numeroCheck: string) {
    try {
      setEtapa("carregando");
      const { data } = await api.get(
        `/marketing/me/playnee-conversas/status/${numeroCheck.replace(/\D/g, "")}`
      );
      setNumeroNormalizado(data?.numero || numeroCheck);
      if (data?.connected) {
        setEtapa("conectado");
      } else {
        setEtapa("numero");
      }
    } catch (error: any) {
      const status = error?.response?.status;
      // 404 = ainda sem canal cadastrado; 400 = formato antigo incompatível.
      // Em ambos, só abrimos o formulário sem mostrar erro.
      if (status === 404 || status === 400) {
        setEtapa("numero");
        return;
      }
      const msg =
        error?.response?.data?.message ||
        "Não foi possível consultar o status atual.";
      setErro(msg);
      setEtapa("numero");
    }
  }

  async function iniciarConexao() {
    setErro(null);
    const chave = normalizarParaChave(numero);
    if (!chave) {
      setErro("Informe um número válido com DDD.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.post("/marketing/me/playnee-conversas/conectar", {
        numero: chave,
      });

      if (!data?.qrcode) {
        setErro("Não foi possível obter o QR Code. Tente novamente.");
        setEtapa("erro");
        return;
      }

      setQrcode(data.qrcode);
      setNumeroNormalizado(data.numero);
      setEtapa("qr");
      iniciarPolling(data.numero);
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        "Não foi possível iniciar a conexão. Tente novamente.";
      setErro(msg);
      setEtapa("erro");
    } finally {
      setLoading(false);
    }
  }

  function iniciarPolling(numeroChave: string) {
    stopPolling();
    deadlineRef.current = Date.now() + STATUS_TIMEOUT_MS;

    pollingRef.current = setInterval(async () => {
      if (deadlineRef.current && Date.now() > deadlineRef.current) {
        stopPolling();
        setErro(
          "Tempo esgotado aguardando a leitura do QR. Gere um novo QR e tente outra vez."
        );
        setEtapa("erro");
        return;
      }

      try {
        const { data } = await api.get(
          `/marketing/me/playnee-conversas/status/${numeroChave.replace(/\D/g, "")}`
        );
        if (data?.connected) {
          stopPolling();
          await api.post(`/marketing/me/playnee-conversas/finalizar/${numeroChave.replace(/\D/g, "")}`).catch(() => {});
          setEtapa("conectado");
          onConnected();
        }
      } catch {
        // erros transitórios no polling são tolerados até o timeout
      }
    }, STATUS_POLL_MS);
  }

  async function desconectar() {
    const alvo = numeroNormalizado || numero;
    if (!alvo) return;
    try {
      setLoading(true);
      await api.post(
        `/marketing/me/playnee-conversas/desconectar/${alvo.replace(/\D/g, "")}`
      );
      setEtapa("numero");
      onConnected();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message || "Falha ao desconectar.";
      setErro(msg);
    } finally {
      setLoading(false);
    }
  }

  const qrcodeSrc = qrcode
    ? qrcode.startsWith("data:")
      ? qrcode
      : `data:image/png;base64,${qrcode}`
    : null;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md">
      <ModalHeader toggle={handleClose}>Conectar Playnee Conversas</ModalHeader>
      <ModalBody>
        {erro && <Alert color="danger">{erro}</Alert>}

        {etapa === "carregando" && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2 mb-0 text-muted">Verificando status…</p>
          </div>
        )}

        {etapa === "numero" && (
          <>
            <Alert color="primary">
              O número abaixo será vinculado ao Playnee Conversas. Após confirmar,
              escaneie o QR Code com o WhatsApp no celular.
            </Alert>
            <FormGroup>
              <Label>Número do WhatsApp</Label>
              <div
                style={{
                  background: "#f6f9fc",
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                  padding: "0.625rem 0.875rem",
                  fontSize: "0.9rem",
                  color: "#495057",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <i className="fab fa-whatsapp text-success" />
                <span style={{ fontWeight: 600 }}>{formatParaPhoneInput(numero) || numero || "--"}</span>
              </div>
              <small className="text-muted mt-1 d-block">
                Para alterar o número, edite seu canal de contato.
              </small>
            </FormGroup>
          </>
        )}

        {etapa === "qr" && (
          <div className="text-center">
            <p className="mb-3">
              Abra o WhatsApp no celular → <b>Aparelhos conectados</b> →{" "}
              <b>Conectar um aparelho</b> e escaneie o QR abaixo.
            </p>
            {qrcodeSrc ? (
              <img
                src={qrcodeSrc}
                alt="QR Code Playnee Conversas"
                style={{ maxWidth: 260, width: "100%" }}
              />
            ) : (
              <Spinner color="primary" />
            )}
            <div className="mt-3">
              <Badge color="warning">Aguardando leitura do QR…</Badge>
            </div>
          </div>
        )}

        {etapa === "conectado" && (
          <div className="text-center">
            <Badge color="success" className="mb-3" style={{ fontSize: 14 }}>
              Conectado
            </Badge>
            <p>
              Número <b>{numeroNormalizado || numero}</b> vinculado ao seu
              usuário no Playnee Conversas.
            </p>
          </div>
        )}

        {etapa === "erro" && (
          <div className="text-center">
            <Badge color="danger" className="mb-3" style={{ fontSize: 14 }}>
              Falha na conexão
            </Badge>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {etapa === "numero" && (
          <>
            <Button color="secondary" onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button color="primary" onClick={iniciarConexao} disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Gerar QR Code"}
            </Button>
          </>
        )}
        {etapa === "qr" && (
          <>
            <Button color="secondary" onClick={handleClose}>
              Fechar
            </Button>
            <Button
              color="primary"
              outline
              onClick={iniciarConexao}
              disabled={loading}
            >
              Gerar novo QR
            </Button>
          </>
        )}
        {etapa === "conectado" && (
          <>
            <Button color="secondary" onClick={handleClose}>
              Fechar
            </Button>
            <Button color="danger" outline onClick={desconectar} disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Desconectar"}
            </Button>
          </>
        )}
        {etapa === "erro" && (
          <>
            <Button color="secondary" onClick={handleClose}>
              Fechar
            </Button>
            <Button color="primary" onClick={() => setEtapa("numero")}>
              Tentar novamente
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}
